import type { Locale } from "./common";
import { isF01OptionalTrackOutcome } from "./f01-forward-copy";
import {
  computeBmiFromAnswers,
  F01_SAFETY_STOP_VALUE,
  MTC_MEN2_KEY,
  PRESCRIPTION_TRANSFER_ACTIVE_CHOICE,
  PRESCRIPTION_TRANSFER_KEY_F01,
  REPRODUCTIVE_STATUS_KEY,
  roundBmi,
  TYPE_1_DIABETES_KEY,
  type BmiQuizAnswerValue,
} from "./bmi-quiz";

// TES-1161 — the F-01 result page, one outcome per answer set, as DATA shared
// by the API (the app's result), the web's /start result and Din resa.
//
// Source of truth: the F-01 Drive document's section
// "KANONISK F-01-RESULTATCOPY — ENDA IMPORTKÄLLA — 2026-10-07" (Emelie
// 2026-10-07). Every Swedish string below is verbatim from that section; any
// older F-01 result copy is legacy and must not be brought back. English is
// ours (proposed, not approved; Ö-104 covers the en-US locale as a whole).
//
// The section's own rule: "Routing, medicinska stoppsignaler och läkarens
// beslutsansvar ändras inte av copyuppdateringen." What it changes is which
// page each existing answer lands on:
//   - 30,0+ / 27,0–29,9 / 25,0–26,9 each get their own heading;
//   - planning a pregnancy and unsure get the individual-assessment heading
//     over the shared meeting module (they used to be an information card on
//     the band's page);
//   - "Vill inte ange" no longer reaches booking: "Ingen bokning eller
//     betalning öppnas före komplettering";
//   - BMI under 25 is a stop with "Avsluta för nu" only (no booking);
//   - pregnant / breastfeeding / trying keep an optional guidance meeting.
//
// TES-1164 (Ö-383, Emelie 2026-10-08) — F-01 asks about type 1 diabetes and
// MTC/MEN 2, and "Ja" lands on §13 / §14 ("Avsluta" only). §2 (27,0–29,9
// with patient-reported comorbidity) is gone for good: "BMI 27,0–29,9: ett
// gemensamt utfall ”Du kan gå vidare till nästa steg” — inget
// samsjuklighetsutfall." F-01 asks nothing about comorbidity.
// Under 18 (§12) is decided from BankID before any answer and lives with
// each client's own under-18 page.
//
// "Du är godkänd för behandling" is reserved for the doctor's published
// decision and appears nowhere in this file.

type Answers = Record<string, unknown>;

export type F01ResultOutcome =
  // §1 — BMI 30,0 or higher.
  | "treatment"
  // §3 — BMI 27,0–29,9, the one outcome of the band (Ö-383: no §2).
  | "next_step"
  // §4 — BMI 25,0–26,9.
  | "individual_bmi"
  // §5 — BMI under 25,0, new treatment only.
  | "stop_bmi"
  // §6 / §7.
  | "individual_planning"
  | "individual_unsure"
  // §8 — "Vill inte ange".
  | "needs_more_info"
  // §9–§11.
  | "stop_pregnant"
  | "stop_breastfeeding"
  | "stop_trying"
  // §13 / §14 (TES-1164).
  | "stop_type_1_diabetes"
  | "stop_mtc_men2"
  // §15.
  | "transfer";

/** The outcomes that end in the shared meeting module and ordinary booking. */
export const F01_MEETING_OUTCOMES: ReadonlySet<F01ResultOutcome> = new Set([
  "treatment",
  "next_step",
  "individual_bmi",
  "individual_planning",
  "individual_unsure",
]);

const REPRODUCTIVE_STOP_OUTCOMES: Record<string, F01ResultOutcome> = {
  pregnant: "stop_pregnant",
  breastfeeding: "stop_breastfeeding",
  trying: "stop_trying",
};

/** The BMI she is shown, one decimal; null when weight or height is missing. */
export function f01ShownBmi(answers: Answers): number | null {
  const raw = computeBmiFromAnswers(
    answers as Record<string, BmiQuizAnswerValue>,
  );
  return raw === null ? null : roundBmi(raw);
}

/**
 * Which canonical page an F-01 answer set lands on. Null only when weight or
 * height is missing on a new-treatment answer set without a reproductive
 * stop (legacy variant D, which the clients complete before any result).
 *
 * The order is the F-01 document's "Routingprioritet": the absolute safety
 * stops first (type 1 diabetes, MTC/MEN 2 and pregnant / breastfeeding /
 * trying stop both tracks, as `routeF01` does), then the patient group ("PRESCRIPTION_TRANSFER har
 * företräde framför BMI-routing"), then BMI. Within new treatment the BMI
 * stop comes before the reproductive answers that do not stop: completing
 * "Vill inte ange", or planning a pregnancy, changes nothing for a BMI under
 * 25.
 */
export function f01ResultOutcome(answers: Answers): F01ResultOutcome | null {
  // TES-1164 — §13 and §14 before the reproductive stops. The document ranks
  // neither over the other, so the page that offers no way to treatment at
  // all wins: a pregnancy page would offer a guidance meeting about a
  // treatment Ella cannot give her in any case. Type 1 diabetes before MTC/MEN
  // 2 is the question order. Every matched stop still reaches the audit and
  // the doctor (routeF01's rule ids, the risk codes).
  if (answers[TYPE_1_DIABETES_KEY] === F01_SAFETY_STOP_VALUE) {
    return "stop_type_1_diabetes";
  }
  if (answers[MTC_MEN2_KEY] === F01_SAFETY_STOP_VALUE) {
    return "stop_mtc_men2";
  }
  const reproductive = answers[REPRODUCTIVE_STATUS_KEY];
  if (typeof reproductive === "string") {
    const stop = REPRODUCTIVE_STOP_OUTCOMES[reproductive];
    if (stop) return stop;
  }
  if (
    answers[PRESCRIPTION_TRANSFER_KEY_F01] ===
    PRESCRIPTION_TRANSFER_ACTIVE_CHOICE
  ) {
    return "transfer";
  }
  const bmi = f01ShownBmi(answers);
  if (bmi === null) return null;
  if (bmi < 25) return "stop_bmi";
  if (reproductive === "prefer_not_to_say") return "needs_more_info";
  if (reproductive === "planning") return "individual_planning";
  if (reproductive === "unsure") return "individual_unsure";
  if (bmi < 27) return "individual_bmi";
  if (bmi < 30) return "next_step";
  return "treatment";
}

// --- copy -------------------------------------------------------------------

interface OutcomeCopy {
  heading: string;
  // The page's paragraphs in her order. `BMI` marks where the BMI line goes
  // when the section places it; a page without the marker gets the line
  // directly under the heading ("Exakt BMI visas för samtliga grupper,
  // inklusive receptövertag och stoppresultat").
  paragraphs: string[];
}

const BMI = "{BMI}";

export interface F01ResultCopy {
  bmi: (value: string) => string;
  outcomes: Record<F01ResultOutcome, OutcomeCopy>;
  meeting: {
    heading: string;
    body: string;
    price: (amount: string) => string;
    noSubscription: string;
    stepsTitle: string;
    steps: string[];
  };
  transferSteps: { title: string; steps: string[] };
  cta: {
    chooseTime: string;
    continueInApp: string;
    answersComeWithYou: string;
    book: string;
    closeForNow: string;
    // §12–§14's "Avsluta".
    close: string;
    completeAnswer: string;
    healthDeclaration: string;
  };
  // TES-1639 (TES-1600 S3) — the positive result when the care meeting is
  // optional (see buildF01ResultPage's `careMeetingOptional`). TES-1646 —
  // without the choice of assessment, which comes after the declaration.
  optionalMeeting: {
    // The web's page: E4 texts 1–3.
    paragraphs: Record<F01OptionalMeetingOutcome, string[]>;
    steps: string[];
    // The app's page: the approved message after a positive F-01, greeting
    // her by first name when there is one.
    afterPositive: (firstName: string | null) => string[];
    // The app's button to the declaration.
    declaration: string;
  };
}

/**
 * The outcomes whose result leads to the declaration on the optional track
 * (isF01OptionalTrackOutcome): the two forward outcomes and, since TES-1649
 * (Ö-1014), the individual assessment at BMI 25,0–26,9.
 */
export type F01OptionalMeetingOutcome =
  | "treatment"
  | "next_step"
  | "individual_bmi";

const GUIDANCE_SV =
  "Om du vill kan du boka ett digitalt vårdmöte för individuell medicinsk vägledning om din situation och möjliga nästa steg. Att boka mötet innebär inte att behandling eller recept kan erbjudas nu.";
const GUIDANCE_EN =
  "If you want, you can book a video consultation for individual medical guidance about your situation and possible next steps. Booking the consultation does not mean that treatment or a prescription can be offered now.";

// TES-1639 — E4 texts 1 and 2 (TES-1600's message), verbatim.
const OPTIONAL_OPENING_SV =
  "Dina svar visar att du kan gå vidare till en medicinsk bedömning. Nästa steg är att fylla i hälsodeklarationen. Läkaren går igenom dina svar och provsvar och hör av sig om ni behöver ses i ett digitalt vårdmöte.";
const OPTIONAL_DECISION_LINE_SV =
  "Läkaren fattar beslutet efter att ha bedömt din hälsodeklaration och nödvändiga provsvar.";
const OPTIONAL_OPENING_EN =
  "Your answers show that you can go on to a medical assessment. The next step is to fill in the health declaration. The doctor goes through your answers and lab results and gets in touch if you need to meet in a video consultation.";
const OPTIONAL_DECISION_LINE_EN =
  "The doctor makes the decision after assessing your health declaration and the necessary lab results.";

// Kirill's paragraph for BMI 25,0–26,9 (the individual_bmi result), shared
// with the optional track's page for the same outcome (TES-1649).
const INDIVIDUAL_BMI_SV =
  "Vid ett BMI mellan 25,0 och 26,9 gör läkaren en individuell helhetsbedömning. Då vägs bland annat central fettfördelning, metabol hälsa, vikthistorik och hur vikten påverkar dig in för att bedöma om behandling kan vara medicinskt motiverad och säker.";
const INDIVIDUAL_BMI_EN =
  "At a BMI between 25.0 and 26.9 the doctor makes an individual overall assessment. Central fat distribution, metabolic health, weight history and how your weight affects you are among the things weighed in to assess whether treatment can be medically justified and safe.";

export const F01_RESULT_COPY: Record<Locale, F01ResultCopy> = {
  sv: {
    bmi: (value) =>
      `Ditt beräknade BMI är ${value}, utifrån den längd och vikt du har angett.`,
    outcomes: {
      treatment: {
        heading: "Du kan gå vidare mot behandling",
        paragraphs: [
          "Dina svar visar att ett vårdmöte är ett relevant nästa steg.",
          BMI,
          "Under ett digitalt vårdmöte går läkaren igenom din hälsa, din viktresa och dina individuella förutsättningar. Om läkaren bedömer att medicinsk behandling passar dig får du en personlig behandlingsplan och kan själv välja om du vill fortsätta genom Ella.",
        ],
      },
      next_step: {
        heading: "Du kan gå vidare till nästa steg",
        paragraphs: [
          "Det finns fler faktorer än BMI som avgör om medicinsk viktbehandling kan vara rätt för dig.",
          `${BMI} Läkaren behöver därför väga in fler delar av din hälsa, din viktresa och dina individuella förutsättningar.`,
        ],
      },
      individual_bmi: {
        heading: "Du kan gå vidare till en individuell bedömning",
        paragraphs: [
          BMI,
          INDIVIDUAL_BMI_SV,
          "Det här är inte ett behandlingsbeslut. Läkaren fattar beslutet efter vårdmötet och efter att nödvändigt medicinskt underlag har bedömts.",
        ],
      },
      stop_bmi: {
        heading: "Ella kan inte erbjuda dig läkemedelsbehandling just nu",
        paragraphs: [
          BMI,
          "Utifrån de uppgifter du har lämnat erbjuder Ella inte läkemedelsbehandling för viktminskning i nuläget. Kontakta din vårdcentral om du vill få hjälp att bedöma din vikt eller hälsa.",
        ],
      },
      individual_planning: {
        heading: "Du kan gå vidare till en individuell bedömning",
        paragraphs: [
          "Du har berättat att du planerar en graviditet. Det utesluter inte automatiskt medicinsk viktbehandling, men tidpunkten för en eventuell behandling behöver bedömas individuellt.",
          "Under vårdmötet går läkaren igenom din situation och hjälper dig att bedöma vilket nästa steg som passar dig.",
        ],
      },
      individual_unsure: {
        heading: "Du kan gå vidare till en individuell bedömning",
        paragraphs: [
          "Du kan fortfarande gå vidare till nästa steg. Eftersom du har angett att du är osäker på din nuvarande situation behöver läkaren gå igenom detta tillsammans med dig innan beslut om behandling fattas.",
        ],
      },
      needs_more_info: {
        heading: "Vi behöver veta lite mer för att du ska kunna gå vidare",
        paragraphs: [
          "För att läkaren ska kunna bedöma om medicinsk viktbehandling är säker och lämplig för dig behöver du besvara den här frågan innan du går vidare. Svaret används som en del av den medicinska säkerhetsbedömningen.",
        ],
      },
      stop_pregnant: {
        heading: "Behandling kan inte startas just nu",
        paragraphs: [
          "Du har berättat att du är gravid. Ella startar inte läkemedelsbehandling för viktminskning under graviditet.",
          GUIDANCE_SV,
        ],
      },
      stop_breastfeeding: {
        heading: "Behandling kan inte startas just nu",
        paragraphs: [
          "Du har berättat att du ammar. Ella startar inte läkemedelsbehandling för viktminskning under pågående amning.",
          GUIDANCE_SV,
        ],
      },
      stop_trying: {
        heading: "Behandling kan inte startas just nu",
        paragraphs: [
          "Du har berättat att du försöker bli gravid. Ella startar inte läkemedelsbehandling för viktminskning samtidigt som du försöker bli gravid.",
          GUIDANCE_SV,
        ],
      },
      stop_type_1_diabetes: {
        heading: "Ella kan inte erbjuda behandling i vårt digitala vårdflöde",
        paragraphs: [
          "Ella erbjuder inte behandling för diabetes typ 1 inom vårt digitala vårdflöde. Vi hänvisar dig till din ordinarie diabetesmottagning.",
        ],
      },
      stop_mtc_men2: {
        heading:
          "Ella kan inte erbjuda läkemedelsbehandling i vårt digitala vårdflöde",
        paragraphs: [
          "Du har angett medullär sköldkörtelcancer, MTC, eller MEN 2 hos dig själv eller en biologisk förälder, ett syskon eller ett barn. Den typen av sjukdomshistoria innebär att de läkemedel som används i Ellas behandling inte är lämpliga inom vårt digitala vårdflöde.",
          "Kontakta din ordinarie vårdgivare om du vill få hjälp att bedöma andra alternativ.",
        ],
      },
      transfer: {
        heading: "Du kan gå vidare med ditt receptövertag",
        paragraphs: [
          "Du har berättat att du redan använder medicinsk viktbehandling och vill fortsätta genom Ella. Nästa steg är att vi lär känna din hälsa och din nuvarande behandling bättre.",
          "Du går vidare direkt med en fördjupad hälsodeklaration och uppgifter om din pågående behandling och ditt recept. Du behöver inte boka ett vårdmöte eller betala en mötesavgift för att läkaren ska granska ditt ärende.",
        ],
      },
    },
    meeting: {
      heading: "Nästa steg: träffa en läkare",
      body: "Under mötet gör läkaren en individuell medicinsk bedömning utifrån din hälsa, din viktresa och hur vikten påverkar dig. Att boka mötet innebär inte att behandling eller recept är garanterat. Du betalar för läkarens individuella medicinska bedömning.",
      price: (amount) => `Digitalt vårdmöte · ${amount}`,
      noSubscription: "Inget abonnemang startar automatiskt när du bokar.",
      stepsTitle: "Så går det till härifrån",
      steps: [
        "Välj tid och betala det digitala vårdmötet.",
        "Fyll i den fördjupade hälsodeklarationen inför mötet.",
        "Träffa läkaren för en individuell medicinsk bedömning.",
        "Lämna blodprov om läkaren bedömer att det behövs.",
        "Om läkaren godkänner behandling väljer du själv om du vill gå vidare med ett abonnemang.",
      ],
    },
    transferSteps: {
      title: "Så går det till härifrån",
      steps: [
        "Fyll i den fördjupade hälsodeklarationen.",
        "Lämna uppgifter om din pågående behandling och ditt recept.",
        "Komplettera om läkaren behöver mer information.",
        "Läkaren granskar om Ella kan ta över behandlingen.",
        "Du får ett tydligt besked i appen.",
      ],
    },
    cta: {
      chooseTime: "Välj tid",
      continueInApp: "Fortsätt i Ella-appen",
      answersComeWithYou: "Dina svar följer med.",
      book: "Boka vårdmöte",
      closeForNow: "Avsluta för nu",
      close: "Avsluta",
      completeAnswer: "Komplettera ditt svar",
      healthDeclaration: "Fortsätt med din hälsodeklaration",
    },
    // TES-1639 — E4 (Ö-953, approved for shipping 2026-10-30; Emelie may
    // adjust later), placed as TES-1600's message put them: text 1 opens the
    // result, text 2 is the decision line, text 3 the steps. The choice is
    // E1 (b) (Ö-950): "Skriftlig bedömning" or "Boka vårdmöte (395 kr)",
    // under text 11's "Vill du hellre träffa läkaren?". The band's own
    // paragraphs stay; only the lines that promise a meeting go.
    optionalMeeting: {
      paragraphs: {
        treatment: [
          OPTIONAL_OPENING_SV,
          BMI,
          OPTIONAL_DECISION_LINE_SV,
        ],
        next_step: [
          OPTIONAL_OPENING_SV,
          "Det finns fler faktorer än BMI som avgör om medicinsk viktbehandling kan vara rätt för dig.",
          `${BMI} Läkaren behöver därför väga in fler delar av din hälsa, din viktresa och dina individuella förutsättningar.`,
          OPTIONAL_DECISION_LINE_SV,
        ],
        // TES-1649 (Ö-1014) — the same composition as next_step: E4's
        // opening, her BMI and Kirill's individual-assessment paragraph, and
        // E4's decision line in place of "efter vårdmötet".
        individual_bmi: [
          OPTIONAL_OPENING_SV,
          BMI,
          INDIVIDUAL_BMI_SV,
          OPTIONAL_DECISION_LINE_SV,
        ],
      },
      steps: [
        "Fyll i hälsodeklarationen.",
        "Ta blodprov om läkaren behöver det.",
        "Läkaren bedömer och ger besked i appen.",
      ],
      // TES-1646 — "GODKÄND COPY OCH RESTPUNKT 2026-10-30 — EFTER POSITIVT
      // F-01" (F-01 result doc on Drive), verbatim; without a name the
      // greeting drops "{förnamn}".
      afterPositive: (firstName) => [
        firstName
          ? `Tack ${firstName} för att du har berättat mer om dig!`
          : "Tack för att du har berättat mer om dig!",
        "Dina svar visar att du kan gå vidare till nästa steg hos Ella. Nu behöver du fylla i din hälsodeklaration. Där berättar du mer om din hälsa och vilka läkemedel du använder, så att jag får underlag för den medicinska bedömningen.",
      ],
      declaration: "Fyll i hälsodeklarationen",
    },
  },
  en: {
    bmi: (value) =>
      `Your estimated BMI is ${value}, based on the height and weight you have given.`,
    outcomes: {
      treatment: {
        heading: "You can go on towards treatment",
        paragraphs: [
          "Your answers show that a video consultation is a relevant next step.",
          BMI,
          "In the consultation the doctor goes through your health, your weight journey and your individual circumstances. If the doctor assesses that medical treatment suits you, you get a personal treatment plan and choose yourself whether to continue through Ella.",
        ],
      },
      next_step: {
        heading: "You can go on to the next step",
        paragraphs: [
          "More factors than BMI decide whether medical weight treatment can be right for you.",
          `${BMI} The doctor therefore needs to weigh in more parts of your health, your weight journey and your individual circumstances.`,
        ],
      },
      individual_bmi: {
        heading: "You can go on to an individual assessment",
        paragraphs: [
          BMI,
          INDIVIDUAL_BMI_EN,
          "This is not a treatment decision. The doctor makes the decision after the consultation and after the necessary medical information has been assessed.",
        ],
      },
      stop_bmi: {
        heading: "Ella cannot offer you medication right now",
        paragraphs: [
          BMI,
          "Based on the information you have given, Ella does not offer weight-loss medication at present. Contact your health centre if you want help assessing your weight or health.",
        ],
      },
      individual_planning: {
        heading: "You can go on to an individual assessment",
        paragraphs: [
          "You have told us that you are planning a pregnancy. That does not automatically rule out medical weight treatment, but the timing of any treatment needs to be assessed individually.",
          "In the consultation the doctor goes through your situation and helps you assess which next step suits you.",
        ],
      },
      individual_unsure: {
        heading: "You can go on to an individual assessment",
        paragraphs: [
          "You can still go on to the next step. Since you have said that you are unsure about your current situation, the doctor needs to go through this with you before any decision about treatment is made.",
        ],
      },
      needs_more_info: {
        heading: "We need to know a little more before you can go on",
        paragraphs: [
          "For the doctor to be able to assess whether medical weight treatment is safe and suitable for you, you need to answer this question before you go on. The answer is used as part of the medical safety assessment.",
        ],
      },
      stop_pregnant: {
        heading: "Treatment cannot be started right now",
        paragraphs: [
          "You have told us that you are pregnant. Ella does not start weight-loss medication during pregnancy.",
          GUIDANCE_EN,
        ],
      },
      stop_breastfeeding: {
        heading: "Treatment cannot be started right now",
        paragraphs: [
          "You have told us that you are breastfeeding. Ella does not start weight-loss medication while you are breastfeeding.",
          GUIDANCE_EN,
        ],
      },
      stop_trying: {
        heading: "Treatment cannot be started right now",
        paragraphs: [
          "You have told us that you are trying to get pregnant. Ella does not start weight-loss medication while you are trying to get pregnant.",
          GUIDANCE_EN,
        ],
      },
      stop_type_1_diabetes: {
        heading: "Ella cannot offer treatment in our digital care pathway",
        paragraphs: [
          "Ella does not offer treatment for type 1 diabetes within our digital care pathway. We refer you to your regular diabetes clinic.",
        ],
      },
      stop_mtc_men2: {
        heading: "Ella cannot offer medication in our digital care pathway",
        paragraphs: [
          "You have reported medullary thyroid cancer, MTC, or MEN 2 in yourself or a biological parent, sibling or child. That kind of medical history means that the medicines used in Ella's treatment are not suitable within our digital care pathway.",
          "Contact your regular healthcare provider if you want help assessing other options.",
        ],
      },
      transfer: {
        heading: "You can go on with your prescription transfer",
        paragraphs: [
          "You have told us that you already use medical weight treatment and want to continue through Ella. The next step is for us to get to know your health and your current treatment better.",
          "You go straight on to an in-depth health declaration and details about your ongoing treatment and your prescription. You do not need to book a video consultation or pay a consultation fee for the doctor to review your case.",
        ],
      },
    },
    meeting: {
      heading: "Next step: meet a doctor",
      body: "In the video consultation the doctor makes an individual medical assessment based on your health, your weight journey and how your weight affects you. Booking the consultation does not mean that treatment or a prescription is guaranteed. You pay for the doctor's individual medical assessment.",
      price: (amount) => `Video consultation · ${amount}`,
      noSubscription: "No subscription starts automatically when you book.",
      stepsTitle: "How it works from here",
      steps: [
        "Choose a time and pay for the consultation.",
        "Fill in the in-depth health declaration before the consultation.",
        "Meet the doctor for an individual medical assessment.",
        "Give a blood test if the doctor decides one is needed.",
        "If the doctor approves treatment, you choose yourself whether to go ahead with a subscription.",
      ],
    },
    transferSteps: {
      title: "How it works from here",
      steps: [
        "Fill in the in-depth health declaration.",
        "Give details about your ongoing treatment and your prescription.",
        "Add more information if the doctor needs it.",
        "The doctor reviews whether Ella can take over the treatment.",
        "You get a clear answer in the app.",
      ],
    },
    cta: {
      chooseTime: "Choose a time",
      continueInApp: "Continue in the Ella app",
      answersComeWithYou: "Your answers come with you.",
      book: "Book a video consultation",
      closeForNow: "Close for now",
      close: "Close",
      completeAnswer: "Complete your answer",
      healthDeclaration: "Continue with your health declaration",
    },
    optionalMeeting: {
      paragraphs: {
        treatment: [
          OPTIONAL_OPENING_EN,
          BMI,
          OPTIONAL_DECISION_LINE_EN,
        ],
        next_step: [
          OPTIONAL_OPENING_EN,
          "More factors than BMI decide whether medical weight treatment can be right for you.",
          `${BMI} The doctor therefore needs to weigh in more parts of your health, your weight journey and your individual circumstances.`,
          OPTIONAL_DECISION_LINE_EN,
        ],
        individual_bmi: [
          OPTIONAL_OPENING_EN,
          BMI,
          INDIVIDUAL_BMI_EN,
          OPTIONAL_DECISION_LINE_EN,
        ],
      },
      steps: [
        "Fill in the health declaration.",
        "Give a blood test if the doctor needs one.",
        "The doctor assesses and gives you an answer in the app.",
      ],
      afterPositive: (firstName) => [
        firstName
          ? `Thank you ${firstName} for telling us more about yourself!`
          : "Thank you for telling us more about yourself!",
        "Your answers show that you can go on to the next step with Ella. Now you need to fill in your health declaration. There you tell us more about your health and the medicines you use, so that I have what I need for the medical assessment.",
      ],
      declaration: "Fill in the health declaration",
    },
  },
};

// --- the page ---------------------------------------------------------------

export type F01ResultPageChannel = "web" | "app";

export type F01ResultActionKind =
  // App: straight into booking (ordinary booking routing).
  | "choose_time"
  // Web: the hand-off into the app, where the same answers are waiting.
  | "continue_in_app"
  // §9–§11: an optional guidance meeting (app: booking; web: the app).
  | "book"
  // §5, §13, §14 and §9–§11's secondary action. §13/§14 label it "Avsluta";
  // the action is the same decision as "Avsluta för nu" (CP-14).
  | "close_for_now"
  // §8: answer the reproductive question.
  | "complete_answer"
  // §15 in the app: on to the health declaration.
  | "health_declaration";

export interface F01ResultAction {
  kind: F01ResultActionKind;
  label: string;
}

export interface F01ResultMeetingModule {
  heading: string;
  body: string;
  // "Digitalt vårdmöte · 395 kr"; null when the caller passed no fee (the
  // amount is never written here).
  price: string | null;
  noSubscription: string;
  stepsTitle: string;
  steps: string[];
}

export interface F01ResultPage {
  outcome: F01ResultOutcome;
  heading: string;
  // In her order, the BMI line among them.
  paragraphs: string[];
  // The BMI line alone (also inside `paragraphs`), null without a BMI.
  bmiText: string | null;
  // The shared meeting module, on every ordinary booking outcome.
  meeting: F01ResultMeetingModule | null;
  // §9–§11's "Prisrad", above the optional booking.
  priceLine: string | null;
  // §15's "Så går det till härifrån".
  steps: { title: string; items: string[] } | null;
  primary: F01ResultAction;
  secondary: F01ResultAction | null;
  // Web only: "Dina svar följer med." under the hand-off.
  handoffNote: string | null;
}

/** Swedish decimal comma, always one decimal ("26,1", "30,0"). */
export function formatF01Bmi(bmi: number, locale: Locale): string {
  const text = bmi.toFixed(1);
  return locale === "en" ? text : text.replace(".", ",");
}

/** A fee in minor units as the price line shows it: "395 kr" / "SEK 395". */
export function formatF01Fee(minor: number, locale: Locale): string {
  const digits = new Intl.NumberFormat(locale === "en" ? "en-GB" : "sv-SE", {
    maximumFractionDigits: 0,
  }).format(Math.round(minor / 100));
  return locale === "en" ? `SEK ${digits}` : `${digits} kr`;
}

function withBmi(paragraphs: string[], bmiText: string | null): string[] {
  const placed = paragraphs.some((p) => p.includes(BMI));
  const lines = placed ? paragraphs : [BMI, ...paragraphs];
  return lines
    .map((p) =>
      bmiText === null ? p.replace(BMI, "").trim() : p.replace(BMI, bmiText),
    )
    .filter((p) => p.length > 0);
}

function actionsFor(
  outcome: F01ResultOutcome,
  channel: F01ResultPageChannel,
  cta: F01ResultCopy["cta"],
): Pick<F01ResultPage, "primary" | "secondary" | "handoffNote"> {
  const web = channel === "web";
  const handoff = web ? cta.answersComeWithYou : null;
  if (F01_MEETING_OUTCOMES.has(outcome)) {
    return {
      primary: web
        ? { kind: "continue_in_app", label: cta.continueInApp }
        : { kind: "choose_time", label: cta.chooseTime },
      secondary: null,
      handoffNote: handoff,
    };
  }
  switch (outcome) {
    case "transfer":
      return {
        primary: web
          ? { kind: "continue_in_app", label: cta.continueInApp }
          : { kind: "health_declaration", label: cta.healthDeclaration },
        secondary: null,
        handoffNote: handoff,
      };
    case "needs_more_info":
      return {
        primary: { kind: "complete_answer", label: cta.completeAnswer },
        secondary: null,
        handoffNote: handoff,
      };
    case "stop_bmi":
      return {
        primary: { kind: "close_for_now", label: cta.closeForNow },
        secondary: null,
        handoffNote: null,
      };
    case "stop_type_1_diabetes":
    case "stop_mtc_men2":
      // §13/§14: "CTA: Avsluta", no meeting. The same decision as §5's
      // "Avsluta för nu", so the saved result and CP-14 behave as for the
      // other stop that offers no booking.
      return {
        primary: { kind: "close_for_now", label: cta.close },
        secondary: null,
        handoffNote: null,
      };
    default:
      // §9–§11.
      return {
        primary: { kind: "book", label: cta.book },
        secondary: { kind: "close_for_now", label: cta.closeForNow },
        handoffNote: null,
      };
  }
}

/**
 * The canonical result page for an F-01 answer set, or null for a
 * new-treatment answer set without weight or height (legacy variant D).
 * `consultFeeMinor` is the meeting fee the API charges (CONSULT_FEE_CENTS;
 * on the web the tenant config's `consultationMinor`, which equals it).
 */
export function buildF01ResultPage(input: {
  answers: Answers;
  locale: Locale;
  channel: F01ResultPageChannel;
  consultFeeMinor?: number;
  // TES-1639 (TES-1600 S3) — the tenant's `careMeetingOptional` is on and
  // the meeting is optional for her (the API: onboardingMeetingOptional, and
  // since TES-1646 a build that claims
  // CLIENT_CONTRACT_CHANGES.assessmentChoice). Only a positive result (BMI
  // 30+ or 27–29.9) and, since TES-1649 (Ö-1014), BMI 25,0–26,9 change;
  // every other outcome keeps its meeting.
  careMeetingOptional?: boolean;
  // TES-1646 — "{förnamn}" in the app's message; the web has none.
  firstName?: string | null;
}): F01ResultPage | null {
  const { answers, locale, channel, consultFeeMinor } = input;
  const outcome = f01ResultOutcome(answers);
  if (outcome === null) return null;
  const copy = F01_RESULT_COPY[locale] ?? F01_RESULT_COPY.sv;
  const bmi = f01ShownBmi(answers);
  const bmiText = bmi === null ? null : copy.bmi(formatF01Bmi(bmi, locale));
  const amount =
    consultFeeMinor === undefined
      ? null
      : formatF01Fee(consultFeeMinor, locale);
  const price = amount === null ? null : copy.meeting.price(amount);
  if (
    input.careMeetingOptional === true &&
    isF01OptionalTrackOutcome(outcome)
  ) {
    return optionalMeetingPage(
      outcome as F01OptionalMeetingOutcome,
      channel,
      copy,
      bmiText,
      input.firstName ?? null,
    );
  }
  const outcomeCopy = copy.outcomes[outcome];
  const reproductiveStop =
    outcome === "stop_pregnant" ||
    outcome === "stop_breastfeeding" ||
    outcome === "stop_trying";
  return {
    outcome,
    heading: outcomeCopy.heading,
    paragraphs: withBmi(outcomeCopy.paragraphs, bmiText),
    bmiText,
    meeting: F01_MEETING_OUTCOMES.has(outcome)
      ? {
          heading: copy.meeting.heading,
          body: copy.meeting.body,
          price,
          noSubscription: copy.meeting.noSubscription,
          stepsTitle: copy.meeting.stepsTitle,
          steps: [...copy.meeting.steps],
        }
      : null,
    priceLine: reproductiveStop ? price : null,
    steps:
      outcome === "transfer"
        ? {
            title: copy.transferSteps.title,
            items: [...copy.transferSteps.steps],
          }
        : null,
    ...actionsFor(outcome, channel, copy.cta),
  };
}

// TES-1639 (TES-1600 S3) — the positive result with the meeting optional:
// the declaration and the doctor's assessment come next.
//
// TES-1646 (Ö-1015) — and no choice between a written assessment and a
// meeting here: that choice follows the submitted declaration, on Hem. The
// app shows the approved message after a positive F-01 and leads to the
// declaration ("Fyll i hälsodeklarationen", the transfer's
// `health_declaration` action). The web keeps E4's texts and hands her to the
// app ("Fortsätt i Ella-appen"), with no meeting line.
function optionalMeetingPage(
  outcome: F01OptionalMeetingOutcome,
  channel: F01ResultPageChannel,
  copy: F01ResultCopy,
  bmiText: string | null,
  firstName: string | null,
): F01ResultPage {
  const optional = copy.optionalMeeting;
  const page = {
    outcome,
    heading: copy.outcomes[outcome].heading,
    bmiText,
    meeting: null,
    priceLine: null,
    secondary: null,
  };
  if (channel === "web") {
    return {
      ...page,
      paragraphs: withBmi(optional.paragraphs[outcome], bmiText),
      steps: { title: copy.meeting.stepsTitle, items: [...optional.steps] },
      primary: { kind: "continue_in_app", label: copy.cta.continueInApp },
      handoffNote: copy.cta.answersComeWithYou,
    };
  }
  // TES-1649 — BMI 25,0–26,9 keeps her BMI and Kirill's paragraph under the
  // approved message, so the page still says why the assessment is
  // individual.
  const own =
    outcome === "individual_bmi"
      ? withBmi(copy.outcomes.individual_bmi.paragraphs.slice(0, 2), bmiText)
      : [];
  return {
    ...page,
    paragraphs: [...optional.afterPositive(firstName), ...own],
    steps: null,
    primary: { kind: "health_declaration", label: optional.declaration },
    handoffNote: null,
  };
}
